import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Toggle } from '../Toggle';
import {
  FLOWPOWER_AFTER_CAP_CENTS,
  FLOWPOWER_DAILY_CAP_KWH,
  FLOWPOWER_DEFAULT_USAGE_CENTS,
  FLOWPOWER_WINDOW_OPTIONS,
  type FlowPowerForm,
  happyHourRateCents,
} from '../../utils/flowpower';

interface FlowPowerPlanFieldsProps {
  value: FlowPowerForm;
  onChange: (next: FlowPowerForm) => void;
  /** Site region/state (e.g. "NSW1") — picks the default Happy Hour rate. */
  region: string | null | undefined;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

/**
 * Flow Power Happy Hour plan settings, shared by registration, add-site
 * onboarding and the site edit dialog.
 */
export function FlowPowerPlanFields({ value, onChange, region }: FlowPowerPlanFieldsProps) {
  const [showAdvanced, setShowAdvanced] = useState(
    value.rateOverrideCents !== '' || value.window !== `${FLOWPOWER_WINDOW_OPTIONS[0].start}-${FLOWPOWER_WINDOW_OPTIONS[0].end}`,
  );
  const set = <K extends keyof FlowPowerForm>(key: K, v: FlowPowerForm[K]) =>
    onChange({ ...value, [key]: v });
  const rate = happyHourRateCents(value, region);
  const windowLabel =
    FLOWPOWER_WINDOW_OPTIONS.find((o) => `${o.start}-${o.end}` === value.window)?.label.replace(
      / \(.*\)$/,
      '',
    ) ?? value.window.replace('-', ' – ');

  return (
    <div className="space-y-3">
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Flow Power Happy Hour pays <strong>{rate}c/kWh</strong> for the first{' '}
          {FLOWPOWER_DAILY_CAP_KWH} kWh you export between {windowLabel} each day,{' '}
          {FLOWPOWER_AFTER_CAP_CENTS}c after that, and nothing at other times. SolanIQ keeps your
          battery charged for the evening, sells into Happy Hour and stops at the{' '}
          {FLOWPOWER_DAILY_CAP_KWH} kWh cap.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Signed up through a Solar Choice installer
          </p>
          <p className="text-xs text-gray-500">Solar Choice installs get an extra 5c/kWh in Happy Hour.</p>
        </div>
        <Toggle
          checked={value.solarChoice}
          onChange={(v) => set('solarChoice', v)}
          aria-label="Solar Choice installer"
        />
      </div>

      <div>
        <label className={labelClass}>Usage rate (c/kWh)</label>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={value.usageRateCents}
          onChange={(e) => set('usageRateCents', e.target.value)}
          placeholder={`e.g. 41.36 — blank uses ${FLOWPOWER_DEFAULT_USAGE_CENTS}c`}
          className={inputClass}
        />
        <p className="mt-1 text-xs text-gray-500">
          The usage rate on your Flow Power price fact sheet or welcome pack (GST inclusive).
        </p>
      </div>

      <button
        type="button"
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
      >
        {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        My plan has a different rate or window
      </button>

      {showAdvanced && (
        <div className="space-y-3 pl-4 border-l-2 border-gray-200 dark:border-gray-600">
          <div>
            <label className={labelClass}>Happy Hour rate (c/kWh)</label>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={value.rateOverrideCents}
              onChange={(e) => set('rateOverrideCents', e.target.value)}
              placeholder={`Blank uses ${happyHourRateCents({ ...value, rateOverrideCents: '' }, region)}c`}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Happy Hour window</label>
            <select
              value={value.window}
              onChange={(e) => set('window', e.target.value)}
              className={inputClass}
            >
              {FLOWPOWER_WINDOW_OPTIONS.map((o) => (
                <option key={`${o.start}-${o.end}`} value={`${o.start}-${o.end}`}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
